import { listActivityLogs, type CoworkActivityLog } from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { useCollectionScope } from "@/cowork/scope-url"
import { useLanguage } from "@/context/language"
import { createEffect, createMemo, createResource, For, on, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { createAuditLogPagination } from "@/pages/audit-log-pagination"

type ActivityCategory = "all" | "files" | "reports" | "workflows" | "approvals" | "agents" | "integrations" | "projects"
type DateRange = "all" | "today" | "7d" | "30d"

export function CoworkActivityPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const [state, setState] = createStore({ search: "", category: "all" as ActivityCategory, date: "30d" as DateRange })
  const [logs, actions] = createResource(
    () => `${context.state.entityId}:${context.state.projectId}:${context.state.periodId}`,
    () =>
      listActivityLogs({
        kind: "audit",
        projectID: context.state.projectId || undefined,
        periodID: context.state.periodId || undefined,
        limit: 250,
      }),
  )
  const filtered = createMemo(() =>
    (logs() ?? []).filter((item) => {
      const query = state.search.trim().toLowerCase()
      if (state.category !== "all" && activityCategory(item) !== state.category) return false
      if (!withinDateRange(item.created_at, state.date)) return false
      if (!query) return true
      return `${item.title} ${item.description ?? ""} ${item.project_name ?? ""} ${item.user_name ?? ""} ${item.user_email ?? ""}`
        .toLowerCase()
        .includes(query)
    }),
  )
  const categories: ActivityCategory[] = [
    "all",
    "files",
    "reports",
    "workflows",
    "approvals",
    "agents",
    "integrations",
    "projects",
  ]

  return (
    <Page
      title={language.t("cowork.enterprise.activity.title")}
      description={language.t("cowork.enterprise.activity.description")}
    >
      <Filters>
        <input
          class="h-10 min-w-[260px] flex-1 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none placeholder:text-v2-text-text-faint"
          value={state.search}
          placeholder={language.t("cowork.enterprise.activity.search")}
          aria-label={language.t("cowork.enterprise.activity.search")}
          onInput={(event) => setState("search", event.currentTarget.value)}
        />
        <select
          class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
          value={state.category}
          aria-label={language.t("cowork.enterprise.activity.actionType")}
          onChange={(event) => setState("category", event.currentTarget.value as ActivityCategory)}
        >
          <For each={categories}>
            {(category) => (
              <option value={category}>
                {language.t(`cowork.enterprise.activity.category.${category}` as keyof typeof import("@/i18n/en").dict)}
              </option>
            )}
          </For>
        </select>
        <DateSelect value={state.date} onChange={(value) => setState("date", value)} />
        <Refresh onClick={() => void actions.refetch()} />
      </Filters>
      <Show
        when={!logs.loading && filtered().length > 0}
        fallback={<Empty loading={logs.loading} text={language.t("cowork.enterprise.activity.empty")} />}
      >
        <div class="space-y-3">
          <For each={filtered()}>{(item) => <ActivityItem item={item} />}</For>
        </div>
      </Show>
    </Page>
  )
}

export function CoworkAuditLogPage() {
  const language = useLanguage()
  const scope = useCollectionScope()
  const [state, setState] = createStore({
    search: "",
    eventType: "",
    actor: "",
    date: "30d" as DateRange,
    expanded: "",
  })
  const logs = createAuditLogPagination((page) =>
    listActivityLogs({
      ...page,
      kind: "audit",
      entityID: scope().companyID || "ALL",
      projectID: scope().projectID,
      companyID: scope().companyID,
    }),
  )
  createEffect(
    on(
      () => [scope().companyID, scope().projectID] as const,
      () => {
        setState({ expanded: "", eventType: "", actor: "" })
        void logs.reset()
      },
    ),
  )
  onCleanup(logs.dispose)
  const eventTypes = createMemo(() => [...new Set(logs.state.items.map((item) => item.event_type))].sort())
  const actors = createMemo(() =>
    [
      ...new Set(
        logs.state.items.map((item) => item.user_email || item.user_name || item.user_id).filter(Boolean) as string[],
      ),
    ].sort(),
  )
  const filtered = createMemo(() =>
    logs.state.items.filter((item) => {
      const actor = item.user_email || item.user_name || item.user_id || ""
      const query = state.search.trim().toLowerCase()
      if (state.eventType && item.event_type !== state.eventType) return false
      if (state.actor && actor !== state.actor) return false
      if (!withinDateRange(item.created_at, state.date)) return false
      if (!query) return true
      return `${sanitizeAuditText(item.event_type)} ${item.project_name ?? ""} ${actor} ${JSON.stringify(sanitizeAuditValue(item.payload))}`
        .toLowerCase()
        .includes(query)
    }),
  )

  return (
    <Page
      title={language.t("cowork.enterprise.audit.title")}
      description={language.t("cowork.enterprise.audit.description")}
    >
      <Filters>
        <input
          class="h-10 min-w-[240px] flex-1 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none placeholder:text-v2-text-text-faint"
          value={state.search}
          placeholder={language.t("cowork.enterprise.audit.search")}
          aria-label={language.t("cowork.enterprise.audit.search")}
          onInput={(event) => setState("search", event.currentTarget.value)}
        />
        <select
          class="h-10 max-w-[220px] rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
          value={state.eventType}
          aria-label={language.t("cowork.enterprise.audit.eventType")}
          onChange={(event) => setState("eventType", event.currentTarget.value)}
        >
          <option value="">{language.t("cowork.enterprise.audit.allEvents")}</option>
          <For each={eventTypes()}>{(eventType) => <option value={eventType}>{eventType}</option>}</For>
        </select>
        <select
          class="h-10 max-w-[220px] rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
          value={state.actor}
          aria-label={language.t("cowork.enterprise.audit.actor")}
          onChange={(event) => setState("actor", event.currentTarget.value)}
        >
          <option value="">{language.t("cowork.enterprise.audit.allActors")}</option>
          <For each={actors()}>{(actor) => <option value={actor}>{actor}</option>}</For>
        </select>
        <DateSelect value={state.date} onChange={(value) => setState("date", value)} />
        <Refresh
          disabled={logs.state.loading}
          onClick={() => {
            setState("expanded", "")
            void logs.reset()
          }}
        />
      </Filters>
      <p class="mb-3 text-[11px] text-v2-text-text-muted">{language.t("cowork.enterprise.audit.loadedHint")}</p>
      <section
        aria-busy={logs.state.loading}
        class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base"
      >
        <Show when={logs.state.error}>
          <p
            role="alert"
            class="border-b border-v2-border-border-base bg-v2-state-bg-danger px-5 py-3 text-[12px] text-v2-state-fg-danger"
          >
            {language.t("cowork.enterprise.audit.loadError")}
          </p>
        </Show>
        <Show
          when={filtered().length > 0}
          fallback={
            <Show when={!logs.state.error || logs.state.items.length > 0}>
              <Empty loading={logs.state.loading} text={language.t("cowork.enterprise.audit.empty")} />
            </Show>
          }
        >
          <div class="overflow-x-auto">
            <table class="w-full min-w-[900px] border-collapse text-left">
              <thead>
                <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                  <th class="px-5 py-3">{language.t("cowork.enterprise.audit.event")}</th>
                  <th class="px-4 py-3">{language.t("cowork.enterprise.audit.actor")}</th>
                  <th class="px-4 py-3">{language.t("cowork.projects.title")}</th>
                  <th class="px-4 py-3">{language.t("cowork.enterprise.audit.date")}</th>
                  <th class="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                <For each={filtered()}>
                  {(item) => (
                    <>
                      <tr class="border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover">
                        <td class="px-5 py-4">
                          <div class="text-[12px] font-medium">{sanitizeAuditText(item.event_type)}</div>
                          <div class="mt-0.5 max-w-sm truncate text-[10px] text-v2-text-text-muted">
                            {sanitizeAuditText(item.description || item.title)}
                          </div>
                        </td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
                          {item.user_email ||
                            item.user_name ||
                            item.user_id ||
                            language.t("cowork.enterprise.audit.system")}
                        </td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
                          {item.project_name || language.t("cowork.enterprise.common.notAvailable")}
                        </td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">{formatDate(item.created_at)}</td>
                        <td class="px-5 py-4 text-right">
                          <button
                            type="button"
                            class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-[11px]"
                            onClick={() => setState("expanded", state.expanded === item.id ? "" : item.id)}
                          >
                            {language.t(
                              state.expanded === item.id
                                ? "cowork.enterprise.audit.hideDetails"
                                : "cowork.enterprise.audit.viewDetails",
                            )}
                          </button>
                        </td>
                      </tr>
                      <Show when={state.expanded === item.id}>
                        <tr class="border-b border-v2-border-border-muted">
                          <td colspan={5} class="bg-v2-background-bg-layer-02 px-5 py-4">
                            <AuditDetails item={item} />
                          </td>
                        </tr>
                      </Show>
                    </>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
        <div class="flex flex-wrap items-center justify-between gap-3 border-t border-v2-border-border-base px-5 py-4">
          <p aria-live="polite" class="text-[11px] text-v2-text-text-muted">
            {language.t("cowork.enterprise.audit.loadedCount", { count: logs.state.items.length })}
          </p>
          <Show when={logs.state.hasMore || logs.state.error}>
            <button
              type="button"
              disabled={logs.state.loading}
              class="rounded-lg border border-v2-border-border-base px-4 py-2 text-[12px] font-medium hover:bg-v2-overlay-simple-overlay-hover disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void (logs.state.items.length ? logs.loadMore() : logs.reset())}
            >
              {language.t(
                logs.state.loading
                  ? "common.loading"
                  : logs.state.error
                    ? "cowork.enterprise.scope.retry"
                    : "common.loadMore",
              )}
            </button>
          </Show>
        </div>
      </section>
    </Page>
  )
}

function Page(props: { title: string; description: string; children: import("solid-js").JSX.Element }) {
  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1400px]">
        <header>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{props.title}</h1>
          <p class="mt-1 max-w-3xl text-[13px] text-v2-text-text-muted">{props.description}</p>
        </header>
        <div class="mt-7">{props.children}</div>
      </div>
    </div>
  )
}

function Filters(props: { children: import("solid-js").JSX.Element }) {
  return <div class="mb-5 flex flex-wrap items-center gap-2">{props.children}</div>
}

function DateSelect(props: { value: DateRange; onChange: (value: DateRange) => void }) {
  const language = useLanguage()
  return (
    <select
      class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
      value={props.value}
      aria-label={language.t("cowork.enterprise.activity.dateRange")}
      onChange={(event) => props.onChange(event.currentTarget.value as DateRange)}
    >
      <option value="all">{language.t("cowork.enterprise.activity.date.all")}</option>
      <option value="today">{language.t("cowork.enterprise.activity.date.today")}</option>
      <option value="7d">{language.t("cowork.enterprise.activity.date.7d")}</option>
      <option value="30d">{language.t("cowork.enterprise.activity.date.30d")}</option>
    </select>
  )
}

function Refresh(props: { onClick: () => void; disabled?: boolean }) {
  const language = useLanguage()
  return (
    <button
      type="button"
      class="h-10 rounded-lg border border-v2-border-border-base px-3 text-[12px] font-medium hover:bg-v2-overlay-simple-overlay-hover disabled:cursor-not-allowed disabled:opacity-50"
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {language.t("cowork.activity.refresh")}
    </button>
  )
}

function ActivityItem(props: { item: CoworkActivityLog }) {
  const language = useLanguage()
  return (
    <article class="flex items-start gap-4 rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-4">
      <span class="grid size-9 shrink-0 place-items-center rounded-lg bg-v2-background-bg-layer-03 text-[11px] font-semibold">
        {activityInitial(props.item)}
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-[13px] font-medium">{businessTitle(props.item, language)}</h2>
          <time class="text-[10px] text-v2-text-text-faint">{formatDate(props.item.created_at)}</time>
        </div>
        <Show when={props.item.description}>
          <p class="mt-1 line-clamp-2 text-[11px] text-v2-text-text-muted">{props.item.description}</p>
        </Show>
        <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-v2-text-text-faint">
          <Show when={props.item.project_name}>
            <span>{props.item.project_name}</span>
          </Show>
          <Show when={props.item.user_email || props.item.user_name}>
            <span>{props.item.user_name || props.item.user_email}</span>
          </Show>
        </div>
      </div>
    </article>
  )
}

function AuditDetails(props: { item: CoworkActivityLog }) {
  const language = useLanguage()
  return (
    <div class="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
      <dl class="space-y-2 text-[11px]">
        <For each={Object.entries(props.item.details ?? {})}>
          {([label, value]) => (
            <div>
              <dt class="text-v2-text-text-faint">{label}</dt>
              <dd class="mt-0.5 break-words text-v2-text-text-muted">{value || "-"}</dd>
            </div>
          )}
        </For>
        <div>
          <dt class="text-v2-text-text-faint">{language.t("cowork.enterprise.audit.eventId")}</dt>
          <dd class="mt-0.5 break-all text-v2-text-text-muted">{props.item.id}</dd>
        </div>
      </dl>
      <div>
        <div class="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
          {language.t("cowork.enterprise.audit.payload")}
        </div>
        <pre class="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 text-[11px] leading-5">
          {JSON.stringify(sanitizeAuditValue(props.item.payload), null, 2)}
        </pre>
      </div>
    </div>
  )
}

function activityCategory(item: CoworkActivityLog): ActivityCategory {
  const type = item.event_type.toLowerCase()
  if (/file|upload/.test(type)) return "files"
  if (/report|artifact/.test(type)) return "reports"
  if (/workflow|period/.test(type)) return "workflows"
  if (/approval|review/.test(type)) return "approvals"
  if (/agent|session|chat|message/.test(type)) return "agents"
  if (/integration|connection|source/.test(type)) return "integrations"
  return "projects"
}

function businessTitle(item: CoworkActivityLog, language: ReturnType<typeof useLanguage>) {
  const category = activityCategory(item)
  if (category === "files")
    return language.t("cowork.enterprise.activity.event.file", { name: item.description || item.title })
  if (category === "reports")
    return language.t("cowork.enterprise.activity.event.report", { name: item.description || item.title })
  if (category === "approvals")
    return language.t("cowork.enterprise.activity.event.approval", { name: item.description || item.title })
  return item.title
}

function activityInitial(item: CoworkActivityLog) {
  const category = activityCategory(item)
  return category === "files"
    ? "F"
    : category === "reports"
      ? "R"
      : category === "workflows"
        ? "W"
        : category === "approvals"
          ? "A"
          : category === "agents"
            ? "AI"
            : "•"
}

function withinDateRange(value: string, range: DateRange) {
  if (range === "all") return true
  const timestamp = new Date(value).getTime()
  const days = range === "today" ? 1 : range === "7d" ? 7 : 30
  return Number.isFinite(timestamp) && timestamp >= Date.now() - days * 86_400_000
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date)
    : ""
}

function sanitizeAuditText(value: string) {
  return value.replace(/opencode/gi, "agent runtime")
}

function sanitizeAuditValue(value: unknown): unknown {
  if (typeof value === "string") return sanitizeAuditText(value)
  if (Array.isArray(value)) return value.map(sanitizeAuditValue)
  if (!value || typeof value !== "object") return value
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [sanitizeAuditText(key), sanitizeAuditValue(item)]),
  )
}

function Empty(props: { loading: boolean; text: string }) {
  return (
    <div class="grid min-h-56 place-items-center rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-8 text-center text-[12px] text-v2-text-text-muted">
      {props.loading ? (
        <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
      ) : (
        props.text
      )}
    </div>
  )
}
